import { SelectInput, SelectInputItem } from '@ads/components-react';
import { CompletedSalesDataByStatus, EventParticipantCustomFieldColumnDto } from '@linka/domain/dtos';
import { OrderStatus, PaymentMethod, type SaleType } from '@linka/domain/enums';
import { eventDashboardQueries } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { formatDate, formatMoney } from '@linka/utils';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { ColumnDef, DataTable } from '../../../data-table/data-table';
import { Breadcrumb } from '../breadcrumb';
import { ExportChartTriggerButton } from '../components';
import { ExportFlowWrapper } from '../export-flow-wrapper';
import {
  DashboardFilters,
  DashboardFiltersDatePicker,
  DashboardFiltersPaymentMethods,
  DashboardFiltersTickets,
  dashboardFiltersAtom,
} from '../filters';
import { useSystemFilters } from '../hooks/use-system-filters';
import { buildTrackingColumns } from './tracking-columns';

interface DetailedSalesProps {
  userEmail?: string;
}

export const buildCustomFieldColumns = (
  customFieldColumns: EventParticipantCustomFieldColumnDto[],
): ColumnDef<CompletedSalesDataByStatus>[] =>
  customFieldColumns.map((column) => ({
    accessorKey: `custom-field-${column.formFieldId}`,
    header: column.label,
    cell: ({ row }) => <span>{row.original.customFieldsByFormFieldId?.[column.formFieldId] ?? ''}</span>,
  }));

const SALE_TYPE_ALL = 'ALL';

export const DetailedSales = ({ userEmail }: DetailedSalesProps) => {
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const dashboardFilters = useAtomValue(dashboardFiltersAtom);
  const systemFilters = useSystemFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const saleType = (searchParams.get('saleType') as SaleType) || undefined;
  const setFilter = (params: Record<string, string>) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(params).forEach(([k, v]) => {
        if (v) {
          next.set(k, v);
        } else {
          next.delete(k);
        }
      });
      return next;
    });
  const { data: sales, isPending } = useQuery(
    eventDashboardQueries.sales(eventId, {
      ...dashboardFilters,
      ...systemFilters,
      ...(saleType ? { saleType } : {}),
    }),
  );
  const { data: customFieldColumns = [] } = useQuery(eventDashboardQueries.customFieldColumns(eventId));
  const { data: trackingColumns = [] } = useQuery(eventDashboardQueries.trackingColumns(eventId));

  const columns: ColumnDef<CompletedSalesDataByStatus>[] = useMemo(
    () => [
      {
        accessorKey: 'orderId',
        header: t('order-id'),
      },
      {
        accessorKey: 'saleDate',
        header: t('date'),
        cell: ({ row }) => <span>{formatDate({ date: row.original.saleDate, dateFormat: 'dd/MM/yyyy HH:mm' })}</span>,
      },
      {
        accessorKey: 'paymentMethod',
        header: t('payment-method'),
        cell: ({ row }) => <span>{t(row.original.paymentMethod as PaymentMethod)}</span>,
      },
      {
        accessorKey: 'ticketTitle',
        header: t('dashboard.sale-column'),
      },
      {
        accessorKey: 'buyerEmail',
        header: t('dashboard.detailed.buyer-email'),
      },
      {
        accessorKey: 'buyerName',
        header: t('dashboard.detailed.buyer-name'),
      },
      {
        accessorKey: 'buyerPhone',
        header: t('dashboard.detailed.buyer-phone'),
      },
      {
        accessorKey: 'participantName',
        header: t('dashboard.detailed.participant-name'),
      },
      {
        accessorKey: 'participantEmail',
        header: t('dashboard.detailed.participant-email'),
      },
      {
        accessorKey: 'participantPhone',
        header: t('dashboard.detailed.participant-phone'),
      },
      {
        accessorKey: 'participantDocument',
        header: t('dashboard.detailed.participant-document'),
      },
      ...buildCustomFieldColumns(customFieldColumns),
      {
        accessorKey: 'saleAmount',
        header: t('dashboard.detailed.sale-amount'),
        cell: ({ row }) => <span>{formatMoney(row.original.saleAmount)}</span>,
      },
      {
        accessorKey: 'couponUsed',
        header: t('dashboard.detailed.coupon-used'),
      },
      {
        accessorKey: 'discountAmount',
        header: t('dashboard.detailed.discount-amount'),
        cell: ({ row }) => <span>{formatMoney(row.original.discountAmount)}</span>,
      },
      {
        accessorKey: 'feeAmount',
        header: t('dashboard.detailed.fee-amount'),
        cell: ({ row }) => <span>{formatMoney(row.original.feeAmount)}</span>,
      },
      {
        accessorKey: 'finalAmount',
        header: t('dashboard.detailed.final-amount'),
        cell: ({ row }) => <span>{formatMoney(row.original.finalAmount)}</span>,
      },
      {
        accessorKey: 'status',
        header: t('dashboard.detailed.order-status'),
        cell: ({ row }) => <span>{t(row.original.status as OrderStatus)}</span>,
      },
      {
        accessorKey: 'transactionId',
        header: t('dashboard.detailed.transaction-id'),
        cell: ({ row }) => <span>{row.original.transactionId ?? ''}</span>,
      },
      ...buildTrackingColumns<CompletedSalesDataByStatus>(trackingColumns),
    ],
    [t, customFieldColumns, trackingColumns],
  );

  return (
    <div>
      <Breadcrumb hideHome />
      <header className='grid sm:grid-cols-[1fr,max-content] items-center gap-y-mini my-xxs'>
        <h1 className='ts-heading-sm sm:ts-heading-md text-ctx-content-title'>{t('dashboard.detailed.sales-data')}</h1>
        <div className='ml-auto sm:ml-0 sm:mr-auto sm:col-start-1'>
          <DashboardFilters>
            <DashboardFiltersDatePicker />
            <DashboardFiltersPaymentMethods />
            <DashboardFiltersTickets />
            <div className='flex flex-col gap-micro'>
              <SelectInput
                label={t('dashboard.sale-type-filter')}
                value={saleType ?? SALE_TYPE_ALL}
                onValueChange={(value) => {
                  setFilter({ saleType: value === SALE_TYPE_ALL ? '' : value, page: '1' });
                }}
                size='md'
                fullWidth
              >
                <SelectInputItem value={SALE_TYPE_ALL}>{t('dashboard.sale-type-all')}</SelectInputItem>
                <SelectInputItem value='TICKET'>{t('dashboard.sale-type-ticket')}</SelectInputItem>
                <SelectInputItem value='PRODUCT'>{t('dashboard.sale-type-product')}</SelectInputItem>
              </SelectInput>
            </div>
          </DashboardFilters>
        </div>
        <ExportChartTriggerButton type='SALES' className='col-span-2 self-end sm:col-span-1' />
      </header>
      <DataTable
        isPending={isPending}
        columns={columns}
        perPage={10}
        total={sales?.total}
        data={sales?.data || []}
        orderBy={systemFilters.orderBy}
        sortOrder={systemFilters.sortOrder}
        onSortChange={(col, dir) => setFilter({ orderBy: col, sortOrder: dir })}
        page={systemFilters.page}
        onPageChange={(page) => setFilter({ page: page.toString() })}
      />
      <ExportFlowWrapper userEmail={userEmail} />
    </div>
  );
};
